import { AfterViewInit, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { DataTableComponent } from 'src/app/@shared/components/data-table/data-table.component';
import { SubscriptionStateService } from 'src/app/@core/services/subscription-state.service';
import { INVOICE_TABLE_COLUMNS, INVOICE_TABLE_CONFIG } from './billing-invoices-table.config';
import { TermUnit } from 'src/app/pricing/models';
import { PaymentService } from 'src/app/contract-management/services/payment.service';

export enum InvoiceType {
    SUBSCRIPTION = 'Subscription',
    TOP_UP = 'Top-up',
    PLAN_CHANGE = 'Plan Change',
    INVITE_USER_PAYMENT = 'Invite User Payment',
    SALES_TOP_UP = 'Sales Top-up',
}

export interface IInvoiceRow {
    id: string;
    date: Date;
    type: InvoiceType;
    total: number | null;
    currency: string;
    status: string;
    url: string | null;
    showAction: boolean;
}

@Component({
    selector: 'app-billing-invoices',
    standalone: true,
    imports: [CommonModule, ButtonComponent, IconComponent, DataTableComponent],
    templateUrl: './billing-invoices.component.html',
})
export class BillingInvoicesComponent implements OnInit, AfterViewInit {

    @ViewChild('dateCellRef') dateCellRef!: TemplateRef<any>;
    @ViewChild('typeCellRef') typeCellRef!: TemplateRef<any>;
    @ViewChild('totalCellRef') totalCellRef!: TemplateRef<any>;
    @ViewChild('statusCellRef') statusCellRef!: TemplateRef<any>;
    @ViewChild('actionCellRef') actionCellRef!: TemplateRef<any>;

    readonly invoiceColumns = INVOICE_TABLE_COLUMNS;
    invoiceTableConfig = { ...INVOICE_TABLE_CONFIG, loading: true };

    invoices: IInvoiceRow[] = [];
    customTemplates: { [key: string]: TemplateRef<any> } = {};

    constructor(
        private router: Router,
        private subscriptionState: SubscriptionStateService,
        private cdr: ChangeDetectorRef,
        private paymentService: PaymentService,
    ) { }

    ngOnInit() {
        this.subscriptionState.refreshSubscriptionDetails().subscribe();
        this.subscriptionState.refreshOrgSubscriptionDetails()
        this.loadInvoices();
    }

    ngAfterViewInit() {
        this.customTemplates = {
            dateCellRef: this.dateCellRef,
            typeCellRef: this.typeCellRef,
            totalCellRef: this.totalCellRef,
            statusCellRef: this.statusCellRef,
            actionCellRef: this.actionCellRef,
        };
        this.cdr.detectChanges();
    }

    get plan() { return this.subscriptionState.orgPlan(); }
    get subscription() { return this.subscriptionState.orgSubscriptionDetails(); }
    get isCancelPending() { return this.subscriptionState.isCancelPending(); }

    get planName(): string {
        return this.plan?.name ?? '—';
    }

    get billingTermLabel(): string {
        const term = this.subscriptionState.orgSubscriptionDetails()?.billing?.term;
        if (term === TermUnit.YEAR) return 'Annual';
        if (term === TermUnit.MONTH) return 'Monthly';
        return '';
    }

    get cancelAtDate(): Date | null {
        const d = this.subscriptionState.orgSubscriptionDetails()?.cancelAtPeriodEndDate;
        return d ? new Date(d) : null;
    }

    get isTrial(): boolean {
        return this.subscriptionState.isTrial();
    }

    get trialEndDate(): Date | null {
        const d = this.subscriptionState.orgSubscriptionDetails()?.trialEndDate;
        return d ? new Date(d) : null;
    }

    get nextBillingDate(): Date | null {
        const d = this.subscriptionState.orgSubscriptionDetails()?.nextBillingDate;
        return d ? new Date(d) : null;
    }

    statusLabel(status: string): string {
        switch (status?.toLowerCase()) {
            case 'paid':
            case 'active': return 'Paid';
            case 'failed':
            case 'cancelled':
            case 'canceled': return 'Failed';
            case 'pending': return 'Pending';
            default: return status || '—';
        }
    }

    goToPricing() {
        this.router.navigate(['/pricing']);
    }

    onViewInvoice(row: IInvoiceRow) {
        if (row.url) {
            window.open(row.url, '_blank');
        }
    }

    statusClass(status: string): string {
        switch (status?.toLowerCase()) {
            case 'paid':
            case 'active': return 'border border-emerald-200 bg-emerald-100 text-emerald-700';
            case 'cancelled':
            case 'canceled': return 'border border-red-200 bg-red-100 text-red-700';
            case 'pending': return 'border border-amber-200 bg-amber-100 text-amber-700';
            default: return 'border border-gray-200 bg-gray-100 text-gray-600';
        }
    }

    private loadInvoices() {
        this.invoiceTableConfig = { ...this.invoiceTableConfig, loading: true };
        this.paymentService.getInvoices().subscribe({
            next: (res) => {
                const data: any[] = res?.data || [];
                this.invoices = data
                    .filter((item: any) => !item.id?.startsWith('trial'))
                    .map((item: any): IInvoiceRow => {
                        const isSalesGrant = item.id?.startsWith('sales-grant');
                        const url: string | null = item.invoiceUrl ?? item.invoicePdf ?? item.receiptUrl ?? null;
                        return {
                            id: item.id,
                            date: new Date(item.createdAt),
                            type: ({ topup: InvoiceType.TOP_UP, plan_change: InvoiceType.PLAN_CHANGE, invite_user_payment: InvoiceType.INVITE_USER_PAYMENT, sales_top_up: InvoiceType.SALES_TOP_UP } as Record<string, InvoiceType>)[item.type] ?? InvoiceType.SUBSCRIPTION,
                            total: item.amountPaid ?? item.amountDue ?? null,
                            currency: item.currency ?? 'USD',
                            status: item.status?.toLowerCase() ?? '—',
                            url,
                            showAction: !isSalesGrant && !!url,
                        };
                    });
                this.invoiceTableConfig = { ...this.invoiceTableConfig, loading: false };
            },
            error: () => {
                this.invoiceTableConfig = { ...this.invoiceTableConfig, loading: false };
            },
        });
    }

}
